import axios from 'axios';
import { type PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';

import AppContext from '@/contexts/appContext';
import type { StaffUserSession } from '@/types/staffSession';

import { removeLocalStorageSession } from './authUtils';
import { LoggedOutError } from './LoggedOutError';

// Global state to hold current context values
let currentAppContext: {
  setPendingRequests?: (fn: (prev: string[]) => string[]) => void;
  host?: string;
  isLoggingOut?: boolean;
  setIsLoggingOut?: (value: boolean) => void;
  setCurrentUser?: (user: StaffUserSession | null) => void;
  setLogoutReason?: (reason: string | null) => void;
} = {};

// Delegates to the React state setter synced in via the component effect below.
export function resetLogoutState() {
  currentAppContext.setIsLoggingOut?.(false);
}

// Set up interceptors immediately at module level
axios.interceptors.request.use(
  (request) => {
    if (currentAppContext.setPendingRequests) {
      currentAppContext.setPendingRequests((prev) => (request.url ? [...prev, request.url] : prev));
    }
    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    if (currentAppContext.setPendingRequests) {
      currentAppContext.setPendingRequests((prev) =>
        prev.filter((url) => url !== response.config.url)
      );
    }
    return response;
  },
  (error) => {
    if (currentAppContext.setPendingRequests) {
      currentAppContext.setPendingRequests((prev) =>
        prev.filter((url) => url !== error.config?.url)
      );
    }
    if (error.response?.status === 401) {
      if (
        !currentAppContext.isLoggingOut &&
        currentAppContext.setCurrentUser &&
        currentAppContext.host
      ) {
        currentAppContext.setIsLoggingOut?.(true);
        const reason: string =
          error.response?.data?.detail ?? 'You have been logged out. Please log in again.';
        currentAppContext.setLogoutReason?.(reason);
        currentAppContext.setCurrentUser(null);
        removeLocalStorageSession(currentAppContext.host);
      }
      return Promise.reject(new LoggedOutError());
    }
    return Promise.reject(error);
  }
);

const AxiosRequestInterceptor = ({ children }: PropsWithChildren) => {
  const appContext = useContext(AppContext);
  const contextRef = useRef(appContext);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    currentAppContext = {
      setPendingRequests: appContext.setPendingRequests,
      host: appContext.host,
      isLoggingOut,
      setIsLoggingOut,
      setCurrentUser: appContext.setCurrentUser,
      setLogoutReason: appContext.setLogoutReason
    };
    contextRef.current = appContext;
  }, [appContext, isLoggingOut]);

  return children;
};

export default AxiosRequestInterceptor;
